import { Button } from '@/components/ui/button'
import {
    SupplierQuote,
    SupplierQuoteStatus,
} from '@Purchasing/Domain/Model/PurchaseProcess'
import { PurchasingDialog } from '@Purchasing/ui/PurchasingDialog'
import { formatBRL, formatLeadTime } from '@Purchasing/Utils/money'
import { unitCostCents } from '@Purchasing/Utils/quoteCost'
import { ArrowRight, Crown } from 'lucide-react'

const statusLabel: Record<SupplierQuoteStatus, string> = {
    REQUESTED: 'Solicitada',
    RECEIVED: 'Recebida',
    REJECTED: 'Rejeitada',
    CANCELED: 'Cancelada',
}

export const supplierNameOf = (quote: SupplierQuote): string =>
    quote.supplierSnapshot?.corporateName ||
    quote.supplierSnapshot?.tradeName ||
    quote.supplierSnapshot?.supplierId ||
    quote.supplierId

interface SelectQuoteModalProps {
    isOpen: boolean
    onClose?: () => void
    quote: SupplierQuote | null
    currentWinner?: SupplierQuote | null
    onConfirm: (quote: SupplierQuote) => void
    loading?: boolean
}

const QuoteSummary = ({ quote, highlight }: { quote: SupplierQuote; highlight?: boolean }) => (
    <div className={highlight ? 'rounded-md border border-brand bg-accent p-4' : 'rounded-md border border-border p-4'}>
        <p className="truncate text-sm font-semibold">{supplierNameOf(quote)}</p>
        <p className="mt-1 font-display text-2xl font-extrabold text-brand-deep tabular-nums">{formatBRL(quote.totalCents)}</p>
        <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
            <dt className="text-muted-foreground">Custo unitário</dt>
            <dd className="text-right font-semibold tabular-nums">{formatBRL(unitCostCents(quote.totalCents, quote.quantity))}</dd>
            <dt className="text-muted-foreground">Quantidade</dt>
            <dd className="text-right tabular-nums">{quote.quantity} un.</dd>
            <dt className="text-muted-foreground">Prazo</dt>
            <dd className="text-right">{formatLeadTime(quote.leadTimeDays)}</dd>
            <dt className="text-muted-foreground">Pagamento</dt>
            <dd className="truncate text-right" title={quote.paymentTerms}>{quote.paymentTerms || '-'}</dd>
            <dt className="text-muted-foreground">Status</dt>
            <dd className="text-right">{statusLabel[quote.status] ?? quote.status}</dd>
        </dl>
    </div>
)

export const SelectQuoteModal = ({
    isOpen,
    onClose,
    quote,
    currentWinner,
    onConfirm,
    loading,
}: SelectQuoteModalProps) => {
    if (!quote) return null
    const isChange = Boolean(currentWinner && currentWinner._id !== quote._id)

    return (
        <PurchasingDialog
            isOpen={isOpen}
            onClose={onClose}
            eyebrow="Cotação vencedora"
            title={isChange ? 'Trocar cotação selecionada' : 'Selecionar cotação'}
            description={
                isChange
                    ? 'A cotação atual deixa de ser a vencedora. A compra será gerada a partir da nova.'
                    : 'Esta cotação passa a ser a vencedora do processo e habilita a geração da compra.'
            }
            footer={
                <>
                    <Button type="button" variant="ghost" onClick={() => onClose?.()}>Cancelar</Button>
                    <Button type="button" data-testid="confirm-select-quote" disabled={loading} onClick={() => onConfirm(quote)} className="min-w-[170px] gap-1.5">
                        <Crown className="h-4 w-4" strokeWidth={1.5} />
                        {loading ? 'Confirmando…' : isChange ? 'Confirmar troca' : 'Confirmar seleção'}
                    </Button>
                </>
            }
        >
            {isChange && currentWinner ? (
                <div className="grid grid-cols-1 items-center gap-3 sm:grid-cols-[1fr_auto_1fr]" data-testid="current-winner-info">
                    <div>
                        <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.04em] text-muted-foreground">Atual</p>
                        <QuoteSummary quote={currentWinner} />
                    </div>
                    <ArrowRight className="mx-auto hidden h-5 w-5 text-muted-foreground sm:block" />
                    <div>
                        <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.04em] text-brand-deep">Nova vencedora</p>
                        <QuoteSummary quote={quote} highlight />
                    </div>
                </div>
            ) : (
                <QuoteSummary quote={quote} highlight />
            )}
        </PurchasingDialog>
    )
}
